import { DownOutlined, FilterOutlined, UpOutlined } from '@ant-design/icons';
import useSize from '@react-hook/size';
import { flexRender, Header, RowData, Table as ReactTable } from '@tanstack/react-table';
import classNames from 'classnames';
import { compact, noop, sum } from 'lodash';
import { Fragment, ReactNode, useCallback, useMemo, useRef, useState } from 'react';

import { getCdnUrlForFile } from '@willow/shared-iso';

import { Alert, Button, Form, Overlay, Popover } from '../../../bootstrap';
import { useComponentVisible } from '../../hooks/useComponentVisible';
import { ButtonLink } from '../Button/ButtonLink';
import { Modal } from '../Modal/Modal';
import { Pagination } from '../Pagination/Pagination';

import s from './Table.module.scss';

export type BaseRow<TData extends Record<string, unknown> = Record<string, unknown>> = TData & {
  id: string;
  meta?: {
    className?: string;
  };
};
declare module '@tanstack/table-core' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    Filter?: () => ReactNode;
    className?: string;
    isDateFilter?: boolean;
    width?: number;
  }
}

export const STICK_COLUMN_CLASSNAME = s.stickyColumn;

export type SharedTableControls = {
  key: string;
  label: React.ReactNode;
  onClick: (...args: any) => void;
}[];

export function Table<TData extends BaseRow>({
  table,
  canSelect,
  selection: { selected, setSelectedId, allSelected, setAllSelected, setSelectedIds, resetSelected } = {
    selected: [],
    setSelectedId: noop,
    allSelected: false,
    setAllSelected: noop,
    setSelectedIds: noop,
    resetSelected: noop,
  },
  hidePagination,
  pagination: { pageIndex, pageSize, totalCount, setPageSize, setPageIndex },
  controls,
  filteredColumns,
  error,
  errorMessage,
  className,
  sideScroll,
  NoResultsElement,
  selectVisibleOnly,
  selectAllLabel,
}: {
  table: ReactTable<TData>;
  error: any;
  errorMessage?: string;
  sideScroll?: boolean;
  hidePagination?: boolean;
  pagination: {
    totalCount: number;
    pageIndex: number;
    pageSize: number;
    setPageIndex: (n: number) => void;
    setPageSize: (n: number) => void;
  };
  canSelect?: boolean;
  selection?: {
    selected: string[];
    setSelectedIds: (id: string[]) => void;
    setSelectedId: (id: string, value: boolean) => void;
    allSelected: boolean;
    setAllSelected: (s: boolean) => void;
    resetSelected: () => void;
  };
  controls?: SharedTableControls;
  filteredColumns: string[];
  className?: string;
  NoResultsElement?: React.ReactNode;
  selectVisibleOnly?: boolean;
  selectAllLabel?: string;
}) {
  const totalSelected = useMemo(
    () => (allSelected ? totalCount : selected.length),
    [selected, totalCount, allSelected],
  );

  const allVisibleSelected = useMemo(() => {
    const visibleRows = table.getRowModel().rows;
    return visibleRows.length > 0 && visibleRows.every((row) => selected.includes(row.original.id));
  }, [table, selected]);

  const headerChecked = selectVisibleOnly ? allVisibleSelected : allSelected;

  const handleSetAllSelected = useCallback(
    (newAllSelected: boolean) => {
      if (newAllSelected) {
        setSelectedIds(table.getRowModel().rows.map((row) => row.original.id) || []);
      } else {
        setSelectedIds([]);
      }

      setAllSelected(selectVisibleOnly ? false : newAllSelected);
    },
    [setAllSelected, setSelectedIds, table, selectVisibleOnly],
  );

  const handleSelectAll = useCallback(() => {
    setSelectedIds(table.getRowModel().rows.map((row) => row.original.id));
    setAllSelected(true);
  }, [setAllSelected, setSelectedIds, table]);

  const [pendingControl, setPendingControl] = useState<SharedTableControls[number] | undefined>();

  const handleControlClick = useCallback(
    (control: SharedTableControls[number]) => {
      if (selectAllLabel && allSelected) {
        setPendingControl(control);
      } else {
        control.onClick();
      }
    },
    [selectAllLabel, allSelected],
  );

  const tableContainerRef = useRef(null);
  const [tableContainerWidth] = useSize(tableContainerRef);
  const tableRef = useRef(null);
  const [tableWidth] = useSize(tableRef);
  const allColumns = table.getVisibleLeafColumns();
  const fixedColumnWidths = useMemo(() => compact(allColumns.map((c) => c.columnDef.meta?.width)), [allColumns]);
  const numOtherColumns = useMemo(
    () => allColumns.length - fixedColumnWidths.length,
    [allColumns.length, fixedColumnWidths.length],
  );
  const columnWidth = useMemo(() => {
    // Only set column widths if the table side-scrolls. Otherwise, let the table size itself on its own
    if (sideScroll) {
      return Math.max((tableWidth - sum(fixedColumnWidths)) / numOtherColumns, 200);
    }
  }, [tableWidth, fixedColumnWidths, numOtherColumns, sideScroll]);

  return (
    <div className={classNames('w-100', className)}>
      <Alert show={!!error} variant="warning" className="my-4">
        {errorMessage || 'There was a problem retrieving data. Please try refreshing the page.'}
      </Alert>

      <div className="w-100 position-relative" ref={tableContainerRef}>
        {/* SELECTION HEADER & CONTROLS */}
        {!!totalSelected && (
          <table className={classNames(s.table, s.multiSelectBar)}>
            <thead>
              <tr>
                <th>
                  <div className="h-100 w-100 d-flex justify-content-between align-items-center">
                    <Form className="d-inline-flex">
                      <Form.Check
                        inline
                        className="ms-2 me-4"
                        name="select-all"
                        type="checkbox"
                        checked={headerChecked}
                        disabled={table.getRowModel().rows.length === 0}
                        onChange={(e) => handleSetAllSelected(e.target.checked)}
                        aria-label="Select all"
                      />
                      <label htmlFor="select-all">({totalSelected}) Selected</label>
                      {(!!selected.length || allSelected) && (
                        <>
                          <div className="border-end u-border-color-bark-2 ms-3 me-3 h-100">&nbsp;</div>
                          <ButtonLink onClick={resetSelected}>Clear</ButtonLink>
                        </>
                      )}
                      {selectAllLabel && !allSelected && totalSelected < totalCount && (
                        <>
                          <div className="border-end u-border-color-bark-2 ms-3 me-3 h-100">&nbsp;</div>
                          <ButtonLink onClick={handleSelectAll}>
                            Select All {totalCount.toLocaleString()} {selectAllLabel}
                          </ButtonLink>
                        </>
                      )}
                    </Form>

                    <div className="d-inline-flex align-items-stretch me-2">
                      {controls &&
                        controls.map((control, i) => (
                          <Fragment key={control.key}>
                            {i > 0 ? (
                              <div className="border-end u-border-color-bark-2 ms-3 me-3 h-100">&nbsp;</div>
                            ) : undefined}
                            <ButtonLink onClick={() => handleControlClick(control)}>{control.label}</ButtonLink>
                          </Fragment>
                        ))}
                    </div>
                  </div>
                </th>
              </tr>
            </thead>
          </table>
        )}

        {/* TABLE DATA */}
        <table className={classNames(s.table, { [s.scrollyTable]: sideScroll })} ref={tableRef}>
          {table.getHeaderGroups().map((headerGroup) => (
            <thead key={headerGroup.id} className="position-relative u-color-bark3">
              <tr>
                {headerGroup.headers.map((header, i) => (
                  <TableHeading
                    key={header.id}
                    i={i}
                    header={header}
                    columnWidth={header.column.columnDef.meta?.width || columnWidth}
                    canSelect={i === 0 && canSelect}
                    disableSelect={table.getRowModel().rows.length === 0}
                    setAllSelected={handleSetAllSelected}
                    allSelected={headerChecked}
                    filteredColumns={filteredColumns}
                  />
                ))}
              </tr>
            </thead>
          ))}

          <tbody>
            {totalCount > 0 && table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.original.id}
                  className={classNames(row.original.meta?.className, {
                    [s.selected]: selected.includes(row.original.id),
                  })}
                >
                  {row.getVisibleCells().map((cell, i) => {
                    const width = cell.column.columnDef.meta?.width || columnWidth;
                    return (
                      <td
                        key={cell.id}
                        className={cell.column.columnDef.meta?.className}
                        style={{
                          width: width ? `${width}px` : 'auto',
                        }}
                      >
                        {canSelect && i === 0 ? (
                          <div className="d-flex align-items-center">
                            <Form>
                              <Form.Check
                                className="m-0 ms-2 me-4"
                                type="checkbox"
                                checked={selected.includes(cell.row.original.id)}
                                onChange={(e) => setSelectedId(cell.row.original.id, e.target.checked)}
                                aria-label="Select row"
                              />
                            </Form>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </div>
                        ) : (
                          flexRender(cell.column.columnDef.cell, cell.getContext())
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              <tr className={s.noResults}>
                <td style={{ width: `${tableContainerWidth}px` }}>
                  <div className="u-fs-3 u-bg-white px-4 py-5 text-center">
                    {NoResultsElement || (
                      <>
                        <img src={getCdnUrlForFile('emptyStreet')} alt="Empty street" />
                        <h3 className="fw-bold mt-3">No Results</h3>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* PAGINATION */}
        {totalCount > 0 && !hidePagination && (
          <Pagination
            pageIndex={pageIndex}
            pageSize={pageSize}
            totalResults={totalCount}
            setPageSize={setPageSize}
            gotoPage={setPageIndex}
          />
        )}
      </div>

      <Modal
        showModal={!!pendingControl}
        onClose={() => setPendingControl(undefined)}
        header={
          <h2 className="mb-0">
            Apply to all {totalCount.toLocaleString()} {selectAllLabel}?
          </h2>
        }
        footer={
          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={() => setPendingControl(undefined)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                pendingControl?.onClick();
                setPendingControl(undefined);
              }}
            >
              {pendingControl?.label}
            </Button>
          </div>
        }
      >
        <p className="mb-0">
          This action will apply to all {totalCount.toLocaleString()} {selectAllLabel} matching your current filters,
          not just the ones on this page.
        </p>
      </Modal>
    </div>
  );
}

const TableHeading = <TData extends BaseRow>({
  i,
  header,
  columnWidth,
  canSelect,
  disableSelect,
  allSelected,
  setAllSelected,
  filteredColumns,
}: {
  i: number;
  header: Header<TData, unknown>;
  columnWidth?: number;
  canSelect?: boolean;
  disableSelect?: boolean;
  allSelected?: boolean;
  setAllSelected: (b: boolean) => void;
  filteredColumns: string[];
}) => {
  const [hover, setHover] = useState(false);
  const [target, setTarget] = useState(null);
  const { ref, isComponentVisible: isFilterOpen, setIsComponentVisible: setIsFilterOpen } = useComponentVisible(false);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setIsFilterOpen(!isFilterOpen);
      setTarget((event as any).target);
    },
    [setIsFilterOpen, isFilterOpen],
  );

  return (
    <th
      className={header.column.columnDef.meta?.className}
      style={{ width: columnWidth ? `${columnWidth}px` : 'auto' }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      ref={ref}
    >
      <div className="d-inline-flex align-items-center gap-1 position-relative">
        <DownOutlined
          className={classNames('position-absolute u-fill-current', {
            invisible: header.column.getIsSorted() !== 'desc',
          })}
          style={{ fontSize: '10px', left: i === 0 ? '-10px' : '-16px', top: '7px' }}
        />
        <UpOutlined
          className={classNames('position-absolute u-fill-current', {
            invisible: header.column.getIsSorted() !== 'asc',
          })}
          style={{ fontSize: '10px', left: i === 0 ? '-10px' : '-16px', top: '6px' }}
        />

        <div className="d-flex align-items-center">
          {canSelect && (
            <Form>
              <Form.Check
                inline
                className="m-0 ms-2 me-4"
                type="checkbox"
                checked={allSelected}
                disabled={disableSelect}
                onChange={(e) => setAllSelected(e.target.checked)}
                aria-label="Select all"
              />
            </Form>
          )}

          {header.column.getCanSort() ? (
            <button
              className="u-btn-reset text-start u-color-current"
              onClick={header.column.getToggleSortingHandler()}
            >
              {flexRender(header.column.columnDef.header, header.getContext())}
            </button>
          ) : (
            flexRender(header.column.columnDef.header, header.getContext())
          )}
        </div>

        {!!header.column.columnDef.meta?.Filter && (
          <button
            className={classNames('u-unset d-flex align-items-center', {
              invisible: !filteredColumns.includes(header.id) && !hover && !isFilterOpen,
              'u-color-bark1': isFilterOpen,
              'u-color-willow': filteredColumns.includes(header.id),
            })}
            onClick={handleClick}
          >
            <FilterOutlined />
          </button>
        )}
      </div>

      {!!header.column.columnDef.meta?.Filter && (
        <Overlay
          show={isFilterOpen}
          target={target}
          placement={i === 0 ? 'bottom-start' : 'bottom'}
          container={ref}
          containerPadding={20}
        >
          <Popover
            className={classNames(s.filterPopover, {
              [s.filterPopoverDate]: header.column.columnDef.meta?.isDateFilter,
            })}
          >
            <Popover.Body>
              <div>{flexRender(header.column.columnDef.meta?.Filter(), header.getContext())}</div>
            </Popover.Body>
          </Popover>
        </Overlay>
      )}
    </th>
  );
};
